import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/pages/Login";
import { Logo } from "@/components/Logo";
import { GoogleAuthButton } from "@/components/GoogleAuthButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";

export default function Register() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ full_name: "", email: "", password: "" });
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  const refCode = params.get("ref") || "";
  useEffect(() => { if (refCode) localStorage.setItem("yva_ref", refCode); }, [refCode]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      toast.error("You must accept the Rules & Regulations to continue.");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, accepted_rules: accepted, ref: refCode || localStorage.getItem("yva_ref") || null });
      localStorage.removeItem("yva_ref");
      login(data.token, data.user);
      toast.success("Profile created! Let's choose your path.");
      navigate("/dashboard/track");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="w-full max-w-sm">
        <Link to="/"><Logo /></Link>
        <h1 className="font-display text-3xl font-bold text-[#0B2447] mt-8">Create your profile</h1>
        <p className="text-slate-500 mt-2">Register with your email to get started.</p>
        {(refCode || localStorage.getItem("yva_ref")) && (
          <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800" data-testid="referral-note">
            🎉 You were referred by a partner — sign up to get started.
          </div>
        )}

        <div className="mt-6">
          <GoogleAuthButton label="Sign up with Google" />
        </div>
        <div className="flex items-center gap-3 my-5">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-xs uppercase tracking-wider text-slate-400">or with email</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="text-slate-700">Full Name</Label>
            <Input required value={form.full_name} onChange={set("full_name")}
              data-testid="register-name" className="mt-1.5 p-3" placeholder="Thabo Mokoena" />
          </div>
          <div>
            <Label className="text-slate-700">Email Address</Label>
            <Input type="email" required value={form.email} onChange={set("email")}
              data-testid="register-email" className="mt-1.5 p-3" placeholder="you@example.com" />
          </div>
          <div>
            <Label className="text-slate-700">Password</Label>
            <Input type="password" required value={form.password} onChange={set("password")}
              data-testid="register-password" className="mt-1.5 p-3" placeholder="At least 6 characters" />
          </div>
          <label className="flex items-start gap-3 cursor-pointer pt-1">
            <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)}
              data-testid="register-accept-rules" className="mt-0.5" />
            <span className="text-sm text-slate-600 leading-relaxed">
              I accept the{" "}
              <Link to="/terms" className="text-[#0B2447] font-semibold underline">Rules & Regulations</Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-[#0B2447] font-semibold underline">Privacy Policy</Link>.
            </span>
          </label>
          <Button type="submit" disabled={loading} data-testid="register-submit"
            className="w-full bg-[#F5C32C] hover:bg-[#E3B220] text-[#0B2447] font-bold py-6 text-base">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Profile"}
          </Button>
        </form>
        <p className="mt-5 text-sm text-slate-500">
          Already registered?{" "}
          <Link to="/login" className="text-[#0B2447] font-semibold underline">Log in</Link>
        </p>
      </div>
    </AuthShell>
  );
}
